import React, { useState, useCallback, memo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import "../index.css";

const ConsentSection = memo(
  ({ isMobile, inputValue, checkboxChecked, setCheckboxChecked }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const handleCheckboxChange = useCallback(() => {
      if (checkboxChecked) {
        setIsModalVisible(true);
        setCheckboxChecked(false);
      } else {
        setCheckboxChecked(true);
      }
    }, [checkboxChecked, setCheckboxChecked]);

    const handleModalClose = useCallback(() => {
      setIsModalVisible(false);
      setCheckboxChecked(true);
    }, [setCheckboxChecked]);

    // Don't render anything if there's no input
    // This helps prevent unnecessary renders
    if (!inputValue) {
      return null;
    }

    return (
      <>
        {/* Consent section */}
        <div
          className="consent-section consent-section-input"
          style={{
            opacity: inputValue ? 1 : 0,
            height: inputValue ? "auto" : 0,
            pointerEvents: inputValue ? "auto" : "none",
          }}
        >
          <div
            className="banner-consent"
            style={{
              fontSize: isMobile ? "10px" : "12px",
              textAlign: "justify",
              lineHeight: isMobile ? "10px" : "1.4",
              borderRadius: "5px 5px 0px 5px",
              marginBottom:
                window.innerWidth >= 768 && window.innerWidth <= 991
                  ? "-60px"
                  : isMobile
                  ? "-70px"
                  : "-85px",
              marginLeft: isMobile ? "0" : "-10px",
              backgroundColor: "#f0f3f4",
              padding: "7px 10px",
              width: isMobile ? "auto" : "560px",
              transition: inputValue ? "all 0.5s ease-in" : "none",
            }}
          >
            <input
              type="checkbox"
              aria-label="Consent to terms and conditions"
              checked={checkboxChecked}
              onChange={handleCheckboxChange}
              style={{
                marginRight: "1px",
                background: "#FFF",
                height: "10px",
                width: "12px",
              }}
            />
            <span style={{ opacity: "0.5" }}>
              By {!isMobile && <>clicking</>} {isMobile && <>tapping</>}{" "}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 75 83"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="consent-svg"
            >
              <path
                d="M27.1706 38.1746C22.5706 38.1746 17.9706 38.1346 13.3606 38.1846C9.46063 38.2246 6.89063 36.4446 6.03063 32.6646C4.40063 25.5446 2.80063 18.4046 1.45063 11.2146C0.760633 7.57462 1.86063 4.30462 5.09063 2.06462C8.45063 -0.265378 11.5606 0.734622 14.8106 2.58462C32.6306 12.7246 50.4806 22.8046 68.3906 32.7946C71.9606 34.7846 74.2406 37.4446 74.2706 41.5746C74.3006 45.7446 71.9406 48.3346 68.3706 50.3146C50.3306 60.3146 32.3406 70.3946 14.3706 80.5246C11.0306 82.4046 7.75063 83.7046 4.36063 80.9746C1.13063 78.3646 0.360633 74.9046 1.33063 70.9146C2.89063 64.5346 4.23063 58.0946 5.90063 51.7346C7.38063 46.1046 8.86063 45.1046 14.5306 45.0846C22.7406 45.0546 30.9506 45.0446 39.1606 45.0246C39.9106 45.0246 40.6706 45.1146 41.4006 44.9946C43.5106 44.6546 46.3406 45.0546 46.3306 41.7846C46.3206 38.6246 43.9606 38.1346 41.3606 38.1446C36.6306 38.1746 31.9106 38.1446 27.1806 38.1446V38.1646L27.1706 38.1746Z"
                fill="white"
              ></path>
            </svg>
            <span style={{ opacity: "0.5" }}>
              , you agree to be bound by (i) Sun West's and (ii) Celligence's
              (collectively "We" or "Us"){" "}
              <a
                href="https://www.angelai.com/terms-and-conditions/"
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions
              </a>
              . Further, you agree to our use of cookies (see{" "}
              <a
                href="https://www.angelai.com/cookie-setting/"
                target="_blank"
                rel="noreferrer"
              >
                Cookie Settings
              </a>
              ) and other technologies to analyze traffic, record sessions,
              personalize content and ads and that this chat is being recorded
              and your personal information will be collected and processed by
              Us, in accordance with our respective{" "}
              <a
                href="https://www.angelai.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              . We and/or our respective service providers may retain a session
              replay and transcript of this chat for quality assurance,
              analysis, product improvement, training and marketing purposes.
            </span>
          </div>
        </div>

        {/* Modal for consent alert */}
        <Modal
          show={isModalVisible}
          onHide={handleModalClose}
          centered
          style={{ textAlign: "justify", justifyContent: "center" }}
          id="consent-modal"
        >
          <Modal.Body style={{ display: "flex", flexDirection: "column" }}>
            <p style={{ fontSize: "12px" }}>
              <b>
                In order to proceed, you are required to agree to this
                paragraph:
              </b>
            </p>
            <p style={{ fontSize: "12px" }}>
              You agree to be bound by (i) Sun West's and (ii) Celligence's
              (collectively "We" or "Us"){" "}
              <a
                target="_blank"
                href="https://angelai.com/terms-and-conditions/"
                rel="noreferrer"
                tabIndex="-1"
              >
                Terms and Conditions
              </a>
              . Further, you agree to our use of cookies (see{" "}
              <a
                href="https://www.angelai.com/cookie-setting/"
                target="_blank"
                rel="noreferrer"
              >
                Cookie Settings
              </a>
              ) and other technologies to analyze traffic, record sessions,
              personalize content and ads and that this chat is being recorded
              and your personal information will be collected and processed by
              Us, in accordance with our respective{" "}
              <a
                target="_blank"
                href="https://angelai.com/privacy-policy/"
                rel="noreferrer"
                tabIndex="-1"
              >
                Privacy Policy
              </a>
              . We and/or our respective service providers may retain a session
              replay and transcript of this chat for quality assurance,
              analysis, product improvement, training and marketing purposes.
            </p>
            <Button
              variant="primary"
              onClick={handleModalClose}
              style={{
                backgroundColor: "rgb(255, 84, 28)",
                width: "100px",
                border: "none",
                margin: "20px auto auto",
              }}
            >
              OK
            </Button>
          </Modal.Body>
        </Modal>
      </>
    );
  }
);

export default ConsentSection;
